import { useFormik } from "formik";
import { ValidationError } from "yup";
import { Modal } from "antd";
import { useCallback } from "react";
import { Button } from "../../common/HtmlTags/Button/Button";
import { Container } from "../../common/HtmlTags/Container/Container";
import { Heading } from "../../common/HtmlTags/Heading/Heading";
import { Paragraph } from "../../common/HtmlTags/Paragraph/Paragraph";
import DatePicker from "../../common/DatePicker/DatePicker";
import { useAppDispatch } from "../../../hooks/redux";
import { completeUserOnboarding } from "../../../store/slices/userSlice";
import { performLogout } from "../../../utils/logoutSession";
import { setLoaderState } from "../../../store/slices/loaderSlice";
import { ErrorToaster } from "../../../helpers/toasterHelper";
import {
  onboardingDobSchema,
  validationErrorToFormikErrors,
} from "../../../utils/validations";
import { isOnboardingAccountRemovedError } from "../../../utils/userOnboarding";
import {
  ERROR_MESSAGES,
  PAGE_INFO_MESSAGES,
  SHARE_LABELS,
} from "../../../utils/constants";
import "./AgeVerificationModal.css";

type AgeVerificationModalProps = {
  open: boolean;
  userId: string;
  onSuccess: () => Promise<void>;
  zIndex?: number;
};

const initialValues = {
  dateOfBirth: "",
  ageConfirmed: false,
};

export default function AgeVerificationModal({
  open,
  userId,
  onSuccess,
  zIndex,
}: AgeVerificationModalProps) {
  const dispatch = useAppDispatch();

  const handleCancel = useCallback(async () => {
    await performLogout(dispatch);
  }, [dispatch]);

  const logoutAfterAccountRemoved = useCallback(
    async (message: string) => {
      ErrorToaster(message);
      await performLogout(dispatch);
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues,
    validate: async (values) => {
      try {
        await onboardingDobSchema.validate(values, { abortEarly: false });
        return {};
      } catch (e) {
        if (e instanceof ValidationError) {
          return validationErrorToFormikErrors(e);
        }
        throw e;
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        dispatch(setLoaderState(true));
        const payload: {
          userId: string;
          ageConfirmed: boolean;
          dob?: string;
        } = {
          userId,
          ageConfirmed: true,
        };
        if (values.dateOfBirth?.trim()) {
          payload.dob = values.dateOfBirth.trim();
        }
        const result = await dispatch(completeUserOnboarding(payload));

        if (completeUserOnboarding.fulfilled.match(result)) {
          await onSuccess();
          return;
        }

        if (completeUserOnboarding.rejected.match(result)) {
          const resultPayload = result.payload;
          const message =
            resultPayload?.message || ERROR_MESSAGES.ONBOARDING.FAILED_TO_COMPLETE_ONBOARDING;
          if (isOnboardingAccountRemovedError(message, resultPayload?.statusCode)) {
            await logoutAfterAccountRemoved(message);
            return;
          }
          ErrorToaster(message);
        }
      } catch {
        ErrorToaster(ERROR_MESSAGES.ONBOARDING.FAILED_TO_COMPLETE_ONBOARDING);
      } finally {
        setSubmitting(false);
        dispatch(setLoaderState(false));
      }
    },
  });

  const ageTouchedOrSubmitted =
    Boolean(formik.touched.ageConfirmed) || formik.submitCount > 0;

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      zIndex={zIndex}
      className="age-verification-modal"
    >
      <Container className="age-verification-modal-content">
        <Heading variant="h3" className="age-verification-modal-title">
          {PAGE_INFO_MESSAGES.AGE_VERIFICATION_MODAL.TITLE}
        </Heading>
        <Paragraph className="age-verification-modal-description">
          {PAGE_INFO_MESSAGES.AGE_VERIFICATION_MODAL.DESCRIPTION}
        </Paragraph>
        <form onSubmit={formik.handleSubmit} noValidate>
          <DatePicker
            formik={formik}
            label={PAGE_INFO_MESSAGES.AGE_VERIFICATION_MODAL.DOB_LABEL}
            labelFor="dateOfBirth"
            wrapperClass="mb-20"
            required={false}
            error={
              formik.touched.dateOfBirth && formik.errors.dateOfBirth
                ? formik.errors.dateOfBirth
                : undefined
            }
          />
          <Container className="age-confirm-wrap mb-20">
            <label className="age-confirm-label" htmlFor="onboardingAgeConfirmed">
              <input
                id="onboardingAgeConfirmed"
                name="ageConfirmed"
                type="checkbox"
                checked={formik.values.ageConfirmed}
                onChange={(e) => {
                  formik.setFieldValue("ageConfirmed", e.target.checked);
                }}
                onBlur={() => formik.setFieldTouched("ageConfirmed", true)}
              />
              <span>I confirm I am 16 or older</span>
            </label>
            {ageTouchedOrSubmitted && formik.errors.ageConfirmed && (
              <Paragraph className="age-confirm-error">
                {formik.errors.ageConfirmed}
              </Paragraph>
            )}
          </Container>
          <Container className="age-verification-modal-actions">
            <Button
              type="submit"
              className="age-verification-modal-submit"
              palette="primary"
              size="large"
              colorScheme="dark"
              hasIcon={false}
              disabled={formik.isSubmitting}
            >
              {SHARE_LABELS.CONTINUE}
            </Button>
            <Button
              type="button"
              className="age-verification-modal-cancel"
              palette="secondary"
              size="large"
              colorScheme="light"
              hasIcon={false}
              disabled={formik.isSubmitting}
              onClick={handleCancel}
            >
              {PAGE_INFO_MESSAGES.AGE_VERIFICATION_MODAL.CANCEL}
            </Button>
          </Container>
        </form>
      </Container>
    </Modal>
  );
}
